import type { CSSProperties, ReactNode } from 'react'
import { useState } from 'react'

import { color, radius, shadow } from '../theme/tokens'

/** Rótulo de seção em versalete. */
export function Eyebrow({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.09em',
        color: color.inkSubtle,
        ...style,
      }}
    >
      {children}
    </span>
  )
}

export function Pill({
  children,
  bg = color.brandTint,
  fg = color.brandInk,
  bd,
  style,
}: {
  children: ReactNode
  bg?: string
  fg?: string
  bd?: string
  style?: CSSProperties
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11.5,
        fontWeight: 600,
        padding: '4px 11px',
        borderRadius: radius.pill,
        background: bg,
        color: fg,
        border: bd ? `1px solid ${bd}` : undefined,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </span>
  )
}

export function Card({
  children,
  style,
  accent,
}: {
  children: ReactNode
  style?: CSSProperties
  accent?: string
}) {
  return (
    <div
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderLeft: accent ? `3px solid ${accent}` : undefined,
        borderRadius: radius.xxl,
        boxShadow: shadow.card,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** Cartão com cabeçalho em faixa creme — o padrão do painel lateral. */
export function PanelCard({
  title,
  right,
  children,
  style,
}: {
  title: string
  right?: ReactNode
  children: ReactNode
  style?: CSSProperties
}) {
  return (
    <div
      style={{
        border: `1px solid ${color.border}`,
        borderRadius: radius.xl,
        overflow: 'hidden',
        background: color.surface,
        ...style,
      }}
    >
      <div
        style={{
          background: color.brandTint,
          padding: '9px 12px',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '.06em',
          color: color.brandInk,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{title}</span>
        {right}
      </div>
      {children}
    </div>
  )
}

export function Toggle({
  on,
  onChange,
  label,
  disabled,
}: {
  on: boolean
  onChange(next: boolean): void
  label: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      style={{
        width: 40,
        height: 22,
        borderRadius: radius.pill,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        flex: 'none',
        transition: 'background .18s ease',
        background: on ? color.green : color.inkGhost,
        opacity: disabled ? 0.5 : 1,
        padding: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left .18s ease',
          left: on ? 21 : 3,
        }}
      />
    </button>
  )
}

export function Button({
  children,
  onClick,
  variant = 'ghost',
  disabled,
  style,
  title,
  type = 'button',
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'ghost' | 'danger' | 'dark'
  disabled?: boolean
  style?: CSSProperties
  title?: string
  type?: 'button' | 'submit'
}) {
  const [hover, setHover] = useState(false)

  const variants: Record<string, CSSProperties> = {
    primary: {
      background: hover && !disabled ? color.brandHover : color.brand,
      color: '#fff',
      border: 'none',
    },
    ghost: {
      background: color.surface,
      color: hover && !disabled ? color.brandHover : color.ink,
      border: `1.5px solid ${hover && !disabled ? color.brand : color.border}`,
    },
    danger: {
      background: hover && !disabled ? color.redTint : color.surface,
      color: color.red,
      border: `1.5px solid ${color.redBorder}`,
    },
    dark: {
      background: hover && !disabled ? '#3a4761' : color.panelHover,
      color: hover && !disabled ? '#fff' : color.panelText,
      border: `1px solid ${color.panelBorder}`,
    },
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        borderRadius: radius.lg,
        padding: '11px 18px',
        fontSize: 12.5,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'background .15s ease, border-color .15s ease, color .15s ease',
        ...variants[variant],
        ...style,
      }}
    >
      {children}
    </button>
  )
}

export function Field({
  label,
  hint,
  children,
  style,
}: {
  label: string
  hint?: ReactNode
  children: ReactNode
  style?: CSSProperties
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        {label}
        {hint ? <span style={{ color: color.inkSubtle, fontWeight: 500 }}>{hint}</span> : null}
      </span>
      {children}
    </label>
  )
}

export const inputStyle: CSSProperties = {
  padding: '10px 12px',
  border: `1px solid ${color.border}`,
  borderRadius: radius.md,
  fontSize: 13,
  background: color.surface,
  color: color.ink,
  width: '100%',
}

export function Check({ size = 13 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      style={{ width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 2.6 }}
    >
      <path d="m4 12 5 5L20 6" />
    </svg>
  )
}

export function Warning({ size = 13 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      style={{ width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 2.2 }}
    >
      <path d="M12 8v5M12 17h.01M10.3 3.9 2.6 17.4A1.6 1.6 0 0 0 4 19.8h16a1.6 1.6 0 0 0 1.4-2.4L13.7 3.9a1.6 1.6 0 0 0-2.8 0Z" />
    </svg>
  )
}

export function Shield({ size = 13 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      style={{ width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 1.9 }}
    >
      <path d="M12 3 4 6v5c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10V6z" />
    </svg>
  )
}

export function Star({ size = 13 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      style={{
        width: size,
        height: size,
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 1.9,
        strokeLinejoin: 'round',
      }}
    >
      <path d="M12 3l2.6 5.6 6.1.6-4.6 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6-4.6-4.1 6.1-.6z" />
    </svg>
  )
}

/** Barra de progresso fina, usada em scores e latências. */
export function Meter({ value, max = 100, tint }: { value: number; max?: number; tint: string }) {
  return (
    <div
      style={{
        height: 5,
        borderRadius: 3,
        background: color.bg,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${Math.max(0, Math.min(100, (value / max) * 100))}%`,
          height: '100%',
          background: tint,
          transition: 'width .4s ease',
        }}
      />
    </div>
  )
}
